<template>
  <Widget
    class="h-100 w-100"
    widgetName="top-keywords"
    translate
    :customParams="customParams"
    @dataFetched="handleDataFetched"
    @locationOptionChange="handleLocationOptionChange"
    @daysOptionChange="handleDaysOptionChange"
  >
    <template v-slot:content>
      <div class="d-flex flex-column w-100 px-6">
        <p
          class="ma-0 pa-0 blue-grey--text text--darken-2 mb-3 font-size-14"
          v-html="$sanitize(subtitle, { allowedTags: ['b'] })"
        />
        <v-row class="my-0 py-0">
          <v-col cols="12" lg="6" md="6" sm="6" xl="6">
            <div
              class="bubbleChart-container"
              :style="{ width: width, height: height }"
            >
              <div ref="bubbleChart" />
            </div>
          </v-col>
          <v-col cols="12" lg="6" md="6" sm="6" xl="6">
            <div
              v-for="(label, index) in labels"
              class="ma-0 pa-0 mb-1 d-flex"
              style="line-height: 12px;"
              :key="label.keyword + index"
            >
              <div class="d-flex me-3">
                <p
                  class="ma-0 pa-0 blue-grey--text font-size-10 font-weight-600 text-end"
                >
                  {{ label.percentage }}%
                </p>
              </div>
              <div class="d-flex justify-start overflow-hidden">
                <p
                  v-snip="1"
                  class="ma-0 pa-0 blue-grey--text text--darken-2 font-size-10 font-weight-400 "
                >
                  {{ label.keyword }}
                </p>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <div id="tooltip-rever" class="tooltip-rever">
        <div class="tooltip-content d-flex">
          <div class="d-flex">
            <div class="tooltip-line"></div>
            <div class="d-flex flex-column ms-2" style="max-width: 366px;">
              <p
                v-if="selectedKeyword.keyword"
                v-snip="1"
                class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-12 font-weight-600"
              >
                {{ selectedKeyword.keyword }}
              </p>
              <div class="d-flex align-center">
                <v-chip x-small class="pa-0 ma-0 me-2" color="blue lighten-2">
                  <div class="d-flex align-center justify-center mx-1 px-1">
                    <p
                      class="ma-0 pa-0 font-size-10 font-weight-400 blue--text"
                      style="line-height: 8px; letter-spacing: 0.4px;"
                    >
                      {{ formatNumber(selectedKeyword.percentage) }}%
                    </p>
                  </div>
                </v-chip>
                <p
                  class="ma-0 pa-0 blue-grey--text text--darken-1 font-size-12 font-weight-400"
                >
                  {{ $t('insights.from_all_content') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Widget>
</template>

<script>
import * as d3 from 'd3'
import Widget from '../SharedComponents/WidgetBase.vue'
import { mapGetters } from 'vuex'
import Vue from 'vue'

export default {
  name: 'KeyWords',
  components: { Widget },

  data() {
    return {
      widgetData: [],
      width: 255,
      height: 160,
      margin: 1,
      colors: [
        '#006796',
        '#0081A1',
        '#0B4D84',
        '#2A316C',
        '#2F9AA9',
        '#5CB2AF',
        '#88C9B6',
        '#B3DFC1',
      ],
      selectedKeyword: { keyword: null, percentage: null },
      locationOption: null,
      customParams: { limit: 8 }, //Limit 8 for custom params because the initial days selection is 30days.
    }
  },

  watch: {
    widgetData: {
      immediate: true,
      handler(val) {
        if (val.length) {
          Vue.nextTick(() => {
            this.createBubbleChart()
          })
        }
      },
    },
  },

  computed: {
    ...mapGetters(['organizationSites']),

    currentSite() {
      return this.organizationSites.find(
        site => site.id === this.locationOption
      )
    },

    subtitle() {
      const key =
        this.locationOption === 'global'
          ? 'keywords_subtitle'
          : 'keywords_site_subtitle'

      return this.$t(`insights.${key}`, {
        keyword: this.labels[0]?.keyword ?? '',
        percentage: this.labels[0]?.percentage ?? '',
        site: this.currentSite?.name ?? null,
      })
    },

    labels() {
      return this.widgetData
        .map(keyword => {
          return {
            ...keyword,
            percentage: this.formatNumber(keyword.percentage),
          }
        })
        .slice(0, 10)
    },
  },

  methods: {
    handleDataFetched(response) {
      const sortedData = response.data.sort(
        (a, b) => b.percentage - a.percentage
      )

      this.widgetData = sortedData.filter(keyword => keyword.percentage > 0)
    },

    handleDaysOptionChange(options) {
      if (options.id === 'l7days' || options.id === 'l30days') {
        this.customParams = { limit: 8 }
      } else {
        this.customParams = { limit: 15 }
      }
    },

    handleLocationOptionChange(id) {
      this.locationOption = id
    },

    createBubbleChart() {
      const svg = this.setupSvg()
      const simulation = this.setupSimulation()

      this.renderBubbles(svg, simulation)
    },

    setupSvg() {
      return d3
        .select(this.$refs.bubbleChart)
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
    },

    formatNumber(value) {
      return Intl.NumberFormat('en-us', {
        notation: 'compact',
        maximumFractionDigits: 3,
      }).format(value)
    },

    setupSimulation() {
      const nodes = this.widgetData.map(d => ({
        ...d,
        radius: Math.max(Math.sqrt(d.percentage) * 3, 15),
      }))

      const largestBubbleRadius = Math.max(...nodes.map(node => node.radius))

      const simulation = d3
        .forceSimulation(nodes)
        .force(
          'center',
          d3.forceCenter(this.width / 2, this.height / 2).strength(0.05)
        )
        .force(
          'x',
          d3
            .forceX(d =>
              d.radius === largestBubbleRadius ? this.width / 2 : d.x
            )
            .strength(d => (d.radius === largestBubbleRadius ? 0.1 : 0.02))
        )
        .force(
          'y',
          d3
            .forceY(d =>
              d.radius === largestBubbleRadius ? this.height / 2 : d.y
            )
            .strength(d => (d.radius === largestBubbleRadius ? 0.1 : 0.02))
        )
        .force(
          'collide',
          d3.forceCollide(d => d.radius + 2)
        )
        .force('boundary', this.boundaryForce(nodes, this.width, this.height))
        .alphaDecay(0.06)
        .alphaMin(0.01)

      for (let i = 0; i < 100; i++) {
        simulation.tick()
      }

      return simulation
    },

    boundaryForce(nodes, width, height) {
      return function() {
        nodes.forEach(node => {
          if (node.x < node.radius || node.x > width - node.radius) {
            node.vx *= -0.5
            node.x = Math.max(
              node.radius,
              Math.min(node.x, width - node.radius)
            )
          }
          if (node.y < node.radius || node.y > height - node.radius) {
            node.vy *= -0.5
            node.y = Math.max(
              node.radius,
              Math.min(node.y, height - node.radius)
            )
          }
        })
      }
    },

    renderBubbles(svg, simulation) {
      const svgWidth = 255
      const svgHeight = 160

      const totalArea = simulation
        .nodes()
        .reduce((sum, d) => sum + Math.PI * Math.pow(d.percentage, 2), 0)

      const maxPossibleRadius =
        Math.sqrt((svgWidth * svgHeight) / (totalArea * Math.PI)) * 10
      const radiusScale = d3
        .scaleSqrt()
        .domain([0, d3.max(simulation.nodes(), d => d.percentage)])
        .range([10, Math.min(maxPossibleRadius, svgWidth / 8)])

      simulation.nodes().forEach(node => {
        node.radius = Math.max(radiusScale(node.percentage), 10)
      })

      const node = svg
        .selectAll('g.bubble')
        .data(simulation.nodes())
        .enter()
        .append('g')
        .attr('class', 'bubble')
        .on('mouseenter', (event, keyword) => {
          this.selectedKeyword = keyword
          const tooltip = d3.select('#tooltip-rever')

          const tooltipWidth = tooltip.node().getBoundingClientRect().width
          const bubbleX = keyword.x
          const bubbleY = keyword.y

          tooltip
            .style('opacity', 1)
            .style('left', `${bubbleX - tooltipWidth / 2}px`)
            .style('top', `${bubbleY + keyword.radius + 140}px`)
        })

        .on('mouseleave', () => {
          d3.select('#tooltip-rever').style('opacity', 0)
        })

      node
        .append('circle')
        .attr('r', d => d.radius)
        .attr('fill', (d, i) => this.colors[i % this.colors.length])

      node
        .append('foreignObject')
        .attr('width', d => Math.floor(d.radius * 2))
        .attr('height', d => Math.floor(d.radius * 2))
        .attr('x', d => -d.radius)
        .attr('y', d => -d.radius)
        .append('xhtml:div')
        .style('display', 'flex')
        .style('flex-direction', 'column')
        .style('justify-content', 'center')
        .style('align-items', 'center')
        .style('text-align', 'center')
        .style('width', '100%')
        .style('height', '100%')
        .style('font-size', d => `${Math.max(d.radius / 4, 7)}px`)
        .style('color', 'white')
        .style('line-height', '1.2')
        .html(d => {
          const words = d.keyword.split(' ')
          const firstWord = words[0]
          const secondWord = words.slice(1).join(' ')

          const tempSpan = document.createElement('span')
          tempSpan.style.fontSize = `${Math.max(d.radius / 4, 7)}px`
          tempSpan.style.whiteSpace = 'nowrap'
          tempSpan.textContent = firstWord

          document.body.appendChild(tempSpan)
          const firstWordWidth = tempSpan.offsetWidth
          document.body.removeChild(tempSpan)

          const bubbleWidth = Math.floor(d.radius * 1.8)
          const showSecondWord = firstWordWidth < bubbleWidth

          return `<div style="width: ${bubbleWidth}px;" class="cursor-default">
              <p class="first-word ma-0 pa-0 cursor-default">${firstWord}</p>
              ${
                showSecondWord && secondWord
                  ? `<p class="second-word ma-0 pa-0 cursor-default">${secondWord}</p>`
                  : ''
              }
            </div>`
        })

      simulation.on('tick', () => {
        node.attr('transform', d => `translate(${d.x}, ${d.y})`)
      })

      simulation
        .force(
          'collide',
          d3.forceCollide(d => d.radius + 2)
        )
        .force('x', d3.forceX(svgWidth / 2).strength(0.1))
        .force('y', d3.forceY(svgHeight / 2).strength(0.1))
        .alpha(1)
        .restart()
    },
  },
}
</script>

<style lang="scss">
.tooltip-rever {
  overflow: hidden;
  max-width: 390px;
  position: absolute;
  color: $color-blue-grey-darken3;
  padding: 8px;
  pointer-events: none;
  font-size: 12px;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 4px;
  border: 1px solid $color-blue-grey-lighten2;
  background: $color-white;
  box-shadow: 0px 3px 1px -2px rgba(113, 128, 150, 0.2),
    0px 2px 2px 0px rgba(113, 128, 150, 0.14),
    0px 1px 4px 0px rgba(113, 128, 150, 0.12);
}

.bubbleChart-container {
  position: relative;
}

.tooltip-line {
  border-radius: 4px;
  background: #88c9b6;
  width: 3px;
  height: 48px;
}

.circle-keyword-label {
  overflow: hidden;
  max-height: 36px;
}

.first-word {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.second-word {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 24px;
}
</style>
